import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import { useRefreshOn } from '../../lib/resourceRefresh';
import type { BillingConfigRead, BillingSubscriptionRead } from '../../types/api';
import { useBusiness } from '../onboarding/useBusiness';
import { BILLING_OFF, fetchBillingConfig, fetchSubscription } from './billingApi';

/** Read once per page session: the keys only change with a deploy. */
let configPromise: Promise<BillingConfigRead> | null = null;

function loadBillingConfig(): Promise<BillingConfigRead> {
  configPromise ??= fetchBillingConfig().catch((caught: unknown) => {
    // Fail closed (no billing buttons), and let the next mount ask again.
    console.warn('Billing config lookup failed.', getErrorMessage(caught));
    configPromise = null;
    return BILLING_OFF;
  });
  return configPromise;
}

export interface BillingState {
  /** Null while the config loads; `enabled: false` when billing is off or unreadable. */
  config: BillingConfigRead | null;
  /** Null until read, and always null while billing is off or `withSubscription` is false. */
  subscription: BillingSubscriptionRead | null;
  /** Puts a fresh read from a billing write in place without a refetch. */
  setSubscription: (subscription: BillingSubscriptionRead) => void;
  /** Re-reads the subscription. */
  reload: () => void;
}

/**
 * Billing config, and the account's subscription when `withSubscription`.
 * Re-reads the subscription whenever the plan is refreshed anywhere
 * (`requestRefresh(_, ['entitlements'])`), so the Plan card, the banner and
 * the upgrade page agree after any billing write.
 */
export function useBilling(withSubscription: boolean): BillingState {
  const { businessId } = useBusiness();
  const [config, setConfig] = useState<BillingConfigRead | null>(null);
  const [subscription, setSubscription] = useState<BillingSubscriptionRead | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  useRefreshOn(businessId, 'entitlements', reload);

  useEffect(() => {
    let cancelled = false;
    void loadBillingConfig().then((value) => {
      if (!cancelled) setConfig(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const shouldRead = withSubscription && config?.enabled === true;

  useEffect(() => {
    if (!shouldRead) return;
    const controller = new AbortController();
    void fetchSubscription(controller.signal)
      .then(setSubscription)
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        console.warn('Subscription lookup failed.', getErrorMessage(caught));
      });
    return () => controller.abort();
  }, [shouldRead, nonce]);

  return {
    config,
    subscription: shouldRead ? subscription : null,
    setSubscription,
    reload,
  };
}
